import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson, deleteJson } from '../lib/api';

// --- Types ---

export type WebhookEvent =
  | 'contact.created'
  | 'contact.updated'
  | 'contact.deleted'
  | 'organization.created'
  | 'organization.updated'
  | 'deal.created'
  | 'deal.updated'
  | 'deal.stage_changed'
  | 'deal.won'
  | 'deal.lost'
  | 'task.completed'
  | 'case.created'
  | 'case.closed'
  | 'lead.created'
  | 'lead.converted';

export interface Webhook {
  id: string;
  url: string;
  events: WebhookEvent[];
  secret?: string;
  isActive: boolean;
  lastTriggeredAt?: string;
  lastStatus?: number;
  failCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface WebhooksResponse {
  webhooks: Webhook[];
  total: number;
}

export interface WebhookTestResult {
  statusCode: number;
  responseTime: number;
  responseBody?: string;
  error?: string;
}

// --- Hooks ---

export function useWebhooks() {
  return useQuery({
    queryKey: ['webhooks'],
    queryFn: () => fetchJson<WebhooksResponse>('/api/webhooks'),
  });
}

export function useCreateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Webhook>) =>
      postJson<Webhook>('/api/webhooks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Webhook> }) =>
      putJson<Webhook>(`/api/webhooks/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteJson(`/api/webhooks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: (id: string) =>
      postJson<WebhookTestResult>(`/api/webhooks/${id}/test`, {}),
  });
}
